import { useState } from 'react';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { CreateExternalIntegrationParams } from 'dashboard/redux/thunk-actions/external-integrations';
import ExternalIntegrationConfiguration from 'dashboard/views/DashboardExternalIntegrationsShowView/ExternalIntegrationConfiguration';
import HubspotConfiguration from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/HubspotConfiguration';
import SalesforceConfiguration from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/SalesforceConfiguration';
import SlackConfiguration from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/SlackConfiguration';
import ZendeskConfiguration from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ZendeskConfiguration';
import { LegacyExternalIntegrationVendor } from 'lib/constants/externalIntegrationVendors';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export interface DashboardExternalIntegrationsShowViewProps {
  isLoading: boolean;
  integrationsEnabled: boolean;
  externalIntegrations: ExternalIntegration[];
  externalIntegrationVendor: LegacyExternalIntegrationVendor;
  archiveExternalIntegration: (externalIntegration: ExternalIntegration) => void;
  createExternalIntegration: (
    externalIntegrationType: ExternalIntegration['type'],
    integrationParams: CreateExternalIntegrationParams,
  ) => void;
}

const DashboardExternalIntegrationsShowView = ({
  isLoading,
  integrationsEnabled,
  externalIntegrations,
  externalIntegrationVendor,
  archiveExternalIntegration,
  createExternalIntegration,
}: DashboardExternalIntegrationsShowViewProps) => {
  const [selectedIntegration, setSelectedIntegration] = useState<ExternalIntegration | null>(null);

  const editable = usePermission(Permission.ExternalIntegrationWrite) && integrationsEnabled;
  const viewable = usePermission(Permission.ExternalIntegrationView);

  const externalIntegrationConfiguration = () => {
    switch (externalIntegrationVendor.model) {
      case ApiModel.ExternalIntegrationProductivityHubspotOAuth:
        return (
          <HubspotConfiguration
            editable={editable}
            isLoading={isLoading}
            externalIntegrations={externalIntegrations}
            createExternalIntegration={createExternalIntegration}
            setSelectedIntegration={setSelectedIntegration}
          />
        );
      case ApiModel.ExternalIntegrationProductivitySalesforceOAuth:
        return (
          <SalesforceConfiguration
            editable={editable}
            isLoading={isLoading}
            externalIntegrations={externalIntegrations}
            createExternalIntegration={createExternalIntegration}
            setSelectedIntegration={setSelectedIntegration}
          />
        );
      case ApiModel.ExternalIntegrationProductivitySlackOAuth:
        return (
          <SlackConfiguration
            editable={editable}
            isLoading={isLoading}
            externalIntegrations={externalIntegrations}
            createExternalIntegration={createExternalIntegration}
            setSelectedIntegration={setSelectedIntegration}
          />
        );
      case ApiModel.ExternalIntegrationProductivityZendeskOAuth:
        return (
          <ZendeskConfiguration
            editable={editable}
            isLoading={isLoading}
            externalIntegrations={externalIntegrations}
            createExternalIntegration={createExternalIntegration}
            setSelectedIntegration={setSelectedIntegration}
          />
        );
      case ApiModel.ExternalIntegrationEnrichmentChainalysis:
      case ApiModel.ExternalIntegrationEnrichmentEquifaxOneview:
      case ApiModel.ExternalIntegrationEnrichmentKyckr:
      case ApiModel.ExternalIntegrationEnrichmentMiddesk:
      case ApiModel.ExternalIntegrationEnrichmentMx:
      case ApiModel.ExternalIntegrationEnrichmentSentilink:
      case ApiModel.ExternalIntegrationEnrichmentTrm:
      case ApiModel.ExternalIntegrationProductivityOpenAi:
        return (
          <ExternalIntegrationConfiguration
            editable={editable}
            isLoading={isLoading}
            externalIntegrations={externalIntegrations}
            externalIntegrationVendor={externalIntegrationVendor}
            createExternalIntegration={createExternalIntegration}
            setSelectedIntegration={setSelectedIntegration}
          />
        );
      case ApiModel.ExternalIntegrationDataSourceCsv:
      case ApiModel.ExternalIntegrationDataSourceMongodb:
      case ApiModel.ExternalIntegrationDataSourceMysql:
      case ApiModel.ExternalIntegrationDataSourcePostgres:
      case ApiModel.ExternalIntegrationDataSourceSnowflake:
      case ApiModel.ExternalIntegrationDataSourceS3:
        return null;
    }
  };

  return (
    <>
      <ModalWrapper
        isOpen={!!selectedIntegration}
        handleCloseModal={() => setSelectedIntegration(null)}
      >
        <ConfirmationModal
          cancelAction={() => setSelectedIntegration(null)}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          confirmAction={() => archiveExternalIntegration(selectedIntegration)}
          header='Delete this connection?'
          confirmActionText='Delete'
        >
          This will revoke Persona&apos;s access and cannot be undone
        </ConfirmationModal>
      </ModalWrapper>
      {viewable && externalIntegrationConfiguration()}
    </>
  );
};

export default DashboardExternalIntegrationsShowView;
